import Image from 'next/image';
import styled, { css } from 'styled-components';
import { ForwardedRef, forwardRef, useEffect, useRef } from 'react';

import {
  Container,
  StyledSection,
  ArticleWrapper,
  ContentContainer,
} from '@components/atoms';
import { Article, SectionHeader } from '@components/molecules';
import { Partner } from '@shared/constants/partnerships';
import { device } from '@styles/mediaQuery';
import useDevice from '@hooks/useDevice';

type PartnershipsProps = { content: Partner[][] };

function Partnerships(
  { content }: PartnershipsProps,
  ref: ForwardedRef<HTMLDivElement | null>
) {
  const { isMobile, isTablet } = useDevice();
  const isPortable = isMobile || isTablet;

  const iconRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (iconRef.current) {
      iconRef.current.scrollLeft = isMobile ? 150 : 80;
    }
  }, [isMobile, isTablet]);

  return (
    <StyledSection ref={ref}>
      <Container>
        <ContentContainer>
          <SectionHeader title="Partnerships" src="icons/green-star.svg" />

          <ArticleWrapper margin={isPortable ? '0 0 30px' : '0 0 50px'}>
            <Article
              align="center"
              titleMaxWidth="450px"
              descriptionMaxWidth="750px"
              wrapAlign="center"
              wrapWidth="100%"
              title="Our Clients"
            />
          </ArticleWrapper>
        </ContentContainer>
      </Container>

      <IconsGroupWrapper ref={iconRef}>
        {content.map((row, ind) => {
          const minWidth = !isPortable ? '1240px' : '750px';

          const properties = !ind
            ? { $minWidth: minWidth }
            : { $minWidth: minWidth, $padding: '0 100px' };

          return (
            <Group {...properties} key={ind}>
              {row.map(({ src, widthM, width, height, heightM, alt }) => (
                <Image
                  loading="lazy"
                  height={isPortable ? heightM : height}
                  width={isPortable ? widthM : width}
                  alt={alt}
                  src={src}
                  key={alt}
                />
              ))}
            </Group>
          );
        })}
      </IconsGroupWrapper>
    </StyledSection>
  );
}

const IconsGroupWrapper = styled.div`
  overflow: hidden;
  transition: 0.3s ease;

  width: 100%;
  max-width: 1240px;
  height: 167px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  margin: 0 auto;
`;

const Group = styled.div<{
  $maxWidth?: string;
  $minWidth?: string;
  $padding?: string;
}>`
  width: 100%;
  height: 40px;
  position: relative;

  display: flex;
  align-items: center;
  justify-content: space-between;

  margin: 0 auto;

  max-width: ${({ $maxWidth }) => $maxWidth};
  min-width: ${({ $minWidth }) => $minWidth};
  padding: ${({ $padding }) => $padding};

  ${device.laptopS(css`
    height: 69px;
  `)}
`;

export default forwardRef<HTMLDivElement | null, PartnershipsProps>(
  Partnerships
);
